// pages/Home.js
//
// Home is the user's own invitations and cards, and nothing else.
//
// This replaces Explore as the landing surface, matching the app
// (src/features/home/screens/HomeScreen.js). The reasoning there applies here
// unchanged: a discovery feed as the first screen asks a brand-new user to
// care about strangers' events before they have any of their own. Partiful and
// Luma both land you on your own stuff; discovery is somewhere you go, not
// somewhere you start.
//
// Explore is not deleted — /explore redirects here, and the page is still in
// the tree.
//
// ── Paging ─────────────────────────────────────────────────────────────────
//
// get_home_invitations returns one page already in display order: open date
// polls, then upcoming soonest first, then past most recent first. Upcoming
// and past share ONE pagination, so past invitations appear only once someone
// scrolls to them instead of the whole history loading up front. That logic
// came over from the old Invitations page and is unchanged; what is new is the
// Cards tab, the tab counts, and the card shapes.
//
// ── Signed out ─────────────────────────────────────────────────────────────
//
// This page no longer bounces a signed-out visitor to /login. Someone who
// followed a link here is shown what the page is for and a way in — and, more
// to the point, a way to start making something without an account first.

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FiRefreshCw } from 'react-icons/fi';
import { IoSparklesOutline, IoAddCircleOutline, IoMailOutline } from 'react-icons/io5';
import AppShell from '../components/AppShell';
import InviteCardLarge from '../components/InviteCardLarge';
import { InviteRow, CardRow } from '../components/HomeRows';
import { useAuth } from '../services/auth';
import supabase from '../services/supabase';
import { firstPageParam, nextPageParam, sectionsFromRows } from '../shared/homePaging';
import '../styles/Home.scss';
import { track } from '../services/track';
import { EV } from '../shared/analyticsEvents';

const FILTERS = [
  { key: 'all', label: 'All' },
  { key: 'hosting', label: 'Hosting', count: 'hosting' },
  { key: 'attending', label: 'Going', count: 'attending' },
  // Cards made and received. A tab rather than a separate page, because a card
  // is the same kind of thing as an invitation to the person looking for it:
  // something someone sent them.
  { key: 'cards', label: 'Cards', count: 'cards' },
];

function Skeleton() {
  return (
    <div className="home-skeletons" aria-hidden="true">
      {[0, 1, 2].map((i) => (
        <div key={i} className="home-skeleton" />
      ))}
    </div>
  );
}

function EmptyState({ filter, signedIn, onCreate, onMakeCard, onSignIn }) {
  if (!signedIn) {
    return (
      <div className="home-empty">
        <IoSparklesOutline className="home-empty-icon" />
        <h2>Your invites live here</h2>
        <p>Everything you are hosting and everything you have been invited to, in one place.</p>
        <div className="home-empty-actions">
          <button type="button" className="home-btn home-btn--primary" onClick={onCreate}>
            Create an invite
          </button>
          <button type="button" className="home-btn" onClick={onSignIn}>
            Sign in
          </button>
        </div>
      </div>
    );
  }

  const isCards = filter === 'cards';
  const isAttending = filter === 'attending';

  return (
    <div className="home-empty">
      {isCards ? (
        <IoMailOutline className="home-empty-icon" />
      ) : (
        <IoAddCircleOutline className="home-empty-icon" />
      )}
      <h2>
        {isCards ? 'No cards yet' : filter === 'hosting' ? 'No events yet' : 'Nothing here yet'}
      </h2>
      <p>
        {isCards
          ? 'Cards you make, and cards people send you, will show up here.'
          : filter === 'hosting'
            ? 'Create your first invite — it takes about a minute.'
            : 'Invitations you receive will show up here.'}
      </p>
      {/* Nothing to offer on Going: you cannot create your way onto someone
          else's guest list. */}
      {!isAttending && (
        <div className="home-empty-actions">
          <button
            type="button"
            className="home-btn home-btn--primary"
            onClick={isCards ? onMakeCard : onCreate}
          >
            {isCards ? 'Make a card' : 'Create an invite'}
          </button>
        </div>
      )}
    </div>
  );
}

export default function Home() {
  const navigate = useNavigate();
  useEffect(() => { track(EV.HOME_VIEWED); }, []);
  const { user, loading: authLoading } = useAuth();

  const [filter, setFilter] = useState('all');
  const [pages, setPages] = useState([]);
  const [counts, setCounts] = useState({ hosting: 0, attending: 0, cards: 0 });
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  const sentinelRef = useRef(null);
  const requestRef = useRef(0);
  // Armed by a real scroll, disarmed by each load — so a page arriving near
  // the bottom cannot trigger the next one, and the next, until the whole
  // history has loaded without anyone scrolling.
  const scrolledRef = useRef(false);

  const isCards = filter === 'cards';

  const loadPage = useCallback(async (page, reset) => {
    if (!user) { setLoading(false); return; }
    const req = ++requestRef.current;
    if (reset) setLoading(true); else setLoadingMore(true);

    try {
      const { data, error } = isCards
        ? await supabase.rpc('get_my_cards', { p_limit: page.limit, p_offset: page.offset })
        : await supabase.rpc('get_home_invitations', {
            p_filter: filter, p_limit: page.limit, p_offset: page.offset,
          });
      if (error) throw error;
      // A tab switch mid-request: drop the stale answer.
      if (req !== requestRef.current) return;

      const next = { rows: data || [], limit: page.limit };
      setPages((prev) => {
        const all = reset ? [next] : [...prev, next];
        setHasMore(!!nextPageParam(next, all));
        return all;
      });
    } catch (err) {
      console.error('Error loading home:', err);
      if (reset) setPages([]);
      setHasMore(false);
    } finally {
      if (req === requestRef.current) {
        setLoading(false);
        setLoadingMore(false);
        setRefreshing(false);
      }
    }
  }, [user, filter, isCards]);

  useEffect(() => {
    if (!user) { setPages([]); setLoading(false); return; }
    setPages([]);
    loadPage(firstPageParam, true);
  }, [user, filter, loadPage]);

  // Counts sit on the tabs, so switching never waits on a whole list to learn
  // how many there are.
  //
  // Called, not watched. Keying this effect on `refreshing` would run it twice
  // per refresh — once when the flag goes up and again when it comes down.
  const loadCounts = useCallback(async () => {
    if (!user) { setCounts({ hosting: 0, attending: 0, cards: 0 }); return; }
    const { data } = await supabase.rpc('get_home_counts');
    if (!data) return;
    setCounts({
      hosting: Number(data.hosting ?? 0),
      attending: Number(data.attending ?? 0),
      cards: Number(data.cards ?? 0),
    });
  }, [user]);

  useEffect(() => { loadCounts(); }, [loadCounts]);

  useEffect(() => {
    const onScroll = () => { scrolledRef.current = true; };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const loadMore = useCallback(() => {
    if (!hasMore || loading || loadingMore || pages.length === 0) return;
    const pageFits = document.documentElement.scrollHeight <= window.innerHeight;
    // A page too short to scroll fills to the screen; otherwise wait for a
    // scroll.
    if (!pageFits && !scrolledRef.current) return;
    scrolledRef.current = false;
    const param = nextPageParam(pages[pages.length - 1], pages);
    if (param) loadPage(param, false);
  }, [hasMore, loading, loadingMore, pages, loadPage]);

  useEffect(() => {
    const el = sentinelRef.current;
    // No observer, no infinite scroll — the first page still renders and the
    // page still works. Constructing one unguarded throws inside the effect,
    // which unmounts the whole screen rather than just losing the paging.
    if (!el || typeof IntersectionObserver === 'undefined') return undefined;
    const observer = new IntersectionObserver(
      (entries) => { if (entries[0]?.isIntersecting) loadMore(); },
      { rootMargin: '300px' },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [loadMore]);

  const refresh = useCallback(() => {
    setRefreshing(true);
    loadCounts();
    loadPage(firstPageParam, true);
  }, [loadPage, loadCounts]);

  const openEvent = useCallback(
    (event) => navigate(`/i/${event.shareable_slug || event.id}`),
    [navigate],
  );
  const openCard = useCallback(
    (card) => navigate(`/c/${card.shareable_slug || card.id}`),
    [navigate],
  );

  const rows = pages.flatMap((p) => p.rows);

  // Cards come back already ordered by last activity, so they are one section;
  // invitations are grouped into Upcoming and Past by the shared helper, which
  // only groups and never re-sorts.
  let sections;
  if (isCards) {
    const seen = new Set();
    const unique = rows.filter((r) => r && !seen.has(r.id) && seen.add(r.id));
    sections = unique.length ? [{ key: 'cards', title: 'Cards', data: unique }] : [];
  } else {
    sections = sectionsFromRows(rows);
  }

  const busy = (loading || authLoading) && !refreshing;
  const empty = !busy && sections.length === 0;

  return (
    <AppShell>
      <div className="home">
        <div className="home-head">
          <h1>Your invites</h1>
          {user && (
            <button
              type="button"
              className="home-refresh"
              onClick={refresh}
              disabled={refreshing}
              aria-label="Refresh"
            >
              <FiRefreshCw size={20} className={refreshing ? 'is-spinning' : ''} />
            </button>
          )}
        </div>

        {user && (
          <div className="home-filters" role="tablist" aria-label="Filter invites">
            {FILTERS.map((f) => {
              const count = f.count ? counts[f.count] : 0;
              return (
                <button
                  key={f.key}
                  type="button"
                  role="tab"
                  aria-selected={filter === f.key}
                  className={`home-filter ${filter === f.key ? 'is-active' : ''}`}
                  onClick={() => setFilter(f.key)}
                >
                  {f.label}
                  {count ? ` ${count}` : ''}
                </button>
              );
            })}
          </div>
        )}

        {busy ? (
          <Skeleton />
        ) : empty ? (
          <EmptyState
            filter={filter}
            signedIn={!!user}
            onCreate={() => navigate('/create-invitation')}
            onMakeCard={() => navigate('/create-greeting-card')}
            onSignIn={() => navigate('/login')}
          />
        ) : (
          <>
            {sections.map((section) => (
              <section key={section.key} className="home-section">
                {/* A single "Upcoming" header with nothing before it is noise;
                    only label a section when there is more than one. */}
                {sections.length > 1 && <h2 className="home-section-title">{section.title}</h2>}

                <div className={`home-list home-list--${section.key}`}>
                  {section.data.map((item) =>
                    section.key === 'cards' ? (
                      <CardRow key={item.id} card={item} onOpen={() => openCard(item)} />
                    ) : section.key === 'past' ? (
                      <InviteRow key={item.id} event={item} onOpen={() => openEvent(item)} />
                    ) : (
                      <InviteCardLarge
                        key={item.id}
                        event={item}
                        onOpen={() => openEvent(item)}
                      />
                    ),
                  )}
                </div>
              </section>
            ))}

            <div ref={sentinelRef} aria-hidden="true" style={{ height: 1 }} />

            {loadingMore && (
              <motion.div className="home-more" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
                <div className="spinner" />
              </motion.div>
            )}
          </>
        )}
      </div>
    </AppShell>
  );
}
